// Phase 5S.C — Recruitment Communications Admin (WhatsApp + Email + Logs)
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  MessageSquare, Mail, ListChecks, RefreshCw, Save, Send, AlertTriangle, CheckCircle2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_LABELS = {
  new: "New Application",
  cv_screen: "CV Screening",
  telephonic: "Telephonic",
  assessment: "Assessment",
  interview: "Interview Scheduled",
  final: "Final Selection",
  offer: "Offer Released",
  bgv: "BGV In-Progress",
  joining: "Joining Confirmed",
  joined: "Onboarded",
  rejected: "Rejected by Us",
  declined: "Declined by Candidate",
};

const AVAILABLE_VARS = [
  "candidate_first_name", "candidate_name", "mobile", "email",
  "designation", "department", "unit", "opening_id",
  "applied_at", "now", "company_name", "stage",
];

const fmt = (ts) => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ts; }
};

const StatusBadge = ({ status }) => {
  const map = {
    SENT: "bg-green-100 text-green-700",
    MOCKED: "bg-amber-100 text-amber-700",
    DELIVERED: "bg-green-100 text-green-700",
    OPENED: "bg-blue-100 text-blue-700",
    CLICKED: "bg-blue-100 text-blue-700",
    FAILED: "bg-red-100 text-red-700",
    BOUNCED: "bg-red-100 text-red-700",
    COMPLAINED: "bg-red-100 text-red-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || "bg-slate-100 text-slate-700"}`}>
      {status || "—"}
    </span>
  );
};

// ============== WhatsApp Configs Tab ==============
function WhatsappTab() {
  const [stageKeys, setStageKeys] = useState([]);
  const [configs, setConfigs] = useState({});
  const [live, setLive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/communication/configs/whatsapp`);
      setStageKeys(data.stage_keys || []);
      setLive(!!data.live);
      const map = {};
      for (const k of (data.stage_keys || [])) {
        map[k] = { event_key: k, campaign_name: "", variable_order: [], enabled: false, description: "" };
      }
      for (const c of (data.configs || [])) {
        map[c.event_key] = { ...map[c.event_key], ...c };
      }
      setConfigs(map);
    } catch (e) {
      toast.error("WhatsApp configs load failed");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateStage = (k, patch) => setConfigs((p) => ({ ...p, [k]: { ...p[k], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/communication/configs/whatsapp`, {
        configs: Object.values(configs),
      });
      toast.success("WhatsApp templates saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="comm-whatsapp-tab">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-600" />
            WhatsApp Templates (AiSensy)
          </h3>
          <p className="text-sm text-muted-foreground">
            Har stage ke liye AiSensy API Campaign name + variable order configure karein. Templates AiSensy par approved hone chahiye.
          </p>
        </div>
        <Badge className={live ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"} data-testid="whatsapp-live-badge">
          {live ? "LIVE" : "MOCK MODE (key missing)"}
        </Badge>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="whatsapp-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="whatsapp-save-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save All"}
        </Button>
      </div>
      {!live && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <b>AISENSY_API_KEY</b> backend .env mein blank hai. Sends mock mode mein chal rahe hain — production ke liye key add karke <code>sudo supervisorctl restart backend</code> chalayein.
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {stageKeys.map((k) => {
          const c = configs[k] || {};
          return (
            <Card key={k} data-testid={`whatsapp-row-${k}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base">{STAGE_LABELS[k] || k}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`wa-en-${k}`} className="text-xs">Enabled</Label>
                    <Switch
                      id={`wa-en-${k}`}
                      checked={!!c.enabled}
                      onCheckedChange={(v) => updateStage(k, { enabled: v })}
                      data-testid={`whatsapp-enabled-${k}`}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <Label className="text-xs">AiSensy Campaign Name</Label>
                  <Input
                    value={c.campaign_name || ""}
                    onChange={(e) => updateStage(k, { campaign_name: e.target.value })}
                    placeholder="e.g., hr_application_received"
                    data-testid={`whatsapp-campaign-${k}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Variables (in template parameter order)</Label>
                  <Input
                    value={(c.variable_order || []).join(", ")}
                    onChange={(e) => updateStage(k, {
                      variable_order: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                    })}
                    placeholder="candidate_first_name, company_name, ..."
                    data-testid={`whatsapp-vars-${k}`}
                  />
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Available: {AVAILABLE_VARS.join(", ")}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Description (optional)</Label>
                  <Input
                    value={c.description || ""}
                    onChange={(e) => updateStage(k, { description: e.target.value })}
                    placeholder="Internal note"
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ============== Email Configs Tab ==============
function EmailTab() {
  const [stageKeys, setStageKeys] = useState([]);
  const [configs, setConfigs] = useState({});
  const [live, setLive] = useState(false);
  const [senderEmail, setSenderEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/communication/configs/email`);
      setStageKeys(data.stage_keys || []);
      setLive(!!data.live);
      setSenderEmail(data.sender_email || "");
      setSenderName(data.sender_name || "");
      const map = {};
      for (const k of (data.stage_keys || [])) {
        map[k] = { event_key: k, subject: "", html_body: "", text_body: "", reply_to: "", enabled: false };
      }
      for (const c of (data.configs || [])) {
        map[c.event_key] = { ...map[c.event_key], ...c };
      }
      setConfigs(map);
    } catch (e) {
      toast.error("Email configs load failed");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateStage = (k, patch) => setConfigs((p) => ({ ...p, [k]: { ...p[k], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/communication/configs/email`, {
        configs: Object.values(configs).map((c) => ({
          ...c, reply_to: c.reply_to || null,
        })),
      });
      toast.success("Email templates saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="comm-email-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Email Templates (Resend)
          </h3>
          <p className="text-sm text-muted-foreground">
            From: <b>{senderName} &lt;{senderEmail}&gt;</b> · Jinja2 variables supported in subject + body — e.g. <code>{`{{ candidate_first_name }}`}</code>
          </p>
        </div>
        <Badge className={live ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"} data-testid="email-live-badge">
          {live ? "LIVE" : "MOCK MODE (key missing)"}
        </Badge>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="email-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="email-save-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save All"}
        </Button>
      </div>
      {!live && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <b>RESEND_API_KEY</b> blank hai backend/.env mein — emails mock mode mein hain.
          </div>
        </div>
      )}
      <div className="space-y-2">
        {stageKeys.map((k) => {
          const c = configs[k] || {};
          const open = expanded === k;
          return (
            <Card key={k} data-testid={`email-row-${k}`}>
              <CardHeader className="py-3 cursor-pointer" onClick={() => setExpanded(open ? null : k)}>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-base">{STAGE_LABELS[k] || k}</CardTitle>
                    <p className="text-xs text-muted-foreground">{c.subject || <em>(no subject set)</em>}</p>
                  </div>
                  <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={!!c.enabled}
                      onCheckedChange={(v) => updateStage(k, { enabled: v })}
                      data-testid={`email-enabled-${k}`}
                    />
                    <Badge variant="outline">{open ? "Hide" : "Edit"}</Badge>
                  </div>
                </div>
              </CardHeader>
              {open && (
                <CardContent className="space-y-2 text-sm">
                  <div>
                    <Label className="text-xs">Subject (Jinja2)</Label>
                    <Input
                      value={c.subject || ""}
                      onChange={(e) => updateStage(k, { subject: e.target.value })}
                      placeholder="Welcome to {{ company_name }} — {{ designation }}"
                      data-testid={`email-subject-${k}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Reply-To (optional)</Label>
                    <Input
                      value={c.reply_to || ""}
                      onChange={(e) => updateStage(k, { reply_to: e.target.value })}
                      placeholder="hr@bravurapackers.com"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">HTML Body</Label>
                    <Textarea
                      rows={9}
                      value={c.html_body || ""}
                      onChange={(e) => updateStage(k, { html_body: e.target.value })}
                      placeholder={`<p>Hi {{ candidate_first_name }},</p>\n<p>...</p>`}
                      data-testid={`email-body-${k}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Plain-Text Body (optional)</Label>
                    <Textarea
                      rows={4}
                      value={c.text_body || ""}
                      onChange={(e) => updateStage(k, { text_body: e.target.value })}
                    />
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Available variables: {AVAILABLE_VARS.join(", ")}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ============== Logs Tab ==============
function LogsTab() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({});
  const [channel, setChannel] = useState("all");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (channel !== "all") params.channel = channel;
      if (status !== "all") params.status = status;
      const [logsRes, sumRes] = await Promise.all([
        axios.get(`${API}/api/communication/logs`, { params }),
        axios.get(`${API}/api/communication/logs/summary`),
      ]);
      setLogs(logsRes.data.logs || []);
      setSummary(sumRes.data.summary || {});
    } catch (e) {
      toast.error("Logs load failed");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [channel, status]);

  const totals = useMemo(() => {
    let s = 0, f = 0, m = 0, d = 0;
    for (const ch of Object.keys(summary || {})) {
      const row = summary[ch] || {};
      s += (row.SENT || 0);
      f += (row.FAILED || 0) + (row.BOUNCED || 0) + (row.COMPLAINED || 0);
      m += (row.MOCKED || 0);
      d += (row.DELIVERED || 0) + (row.OPENED || 0) + (row.CLICKED || 0);
    }
    return { s, f, m, d };
  }, [summary]);

  return (
    <div className="space-y-4" data-testid="comm-logs-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center"><div className="text-xs text-muted-foreground">Sent</div><div className="text-2xl font-bold text-green-700">{totals.s}</div></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><div className="text-xs text-muted-foreground">Delivered/Opened</div><div className="text-2xl font-bold text-blue-700">{totals.d}</div></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><div className="text-xs text-muted-foreground">Failed</div><div className="text-2xl font-bold text-red-700">{totals.f}</div></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><div className="text-xs text-muted-foreground">Mocked</div><div className="text-2xl font-bold text-amber-700">{totals.m}</div></CardContent></Card>
      </div>
      <div className="flex items-center gap-3">
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="w-36" data-testid="logs-channel-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Channels</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="email">Email</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36" data-testid="logs-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="SENT">Sent</SelectItem>
            <SelectItem value="DELIVERED">Delivered</SelectItem>
            <SelectItem value="MOCKED">Mocked</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
            <SelectItem value="BOUNCED">Bounced</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <div className="text-sm text-muted-foreground ml-auto">{logs.length} entries</div>
      </div>
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs">
            <tr>
              <th className="p-2">When</th>
              <th className="p-2">Channel</th>
              <th className="p-2">Stage</th>
              <th className="p-2">To</th>
              <th className="p-2">Campaign / Subject</th>
              <th className="p-2">Status</th>
              <th className="p-2">Error</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && (
              <tr><td colSpan="7" className="p-6 text-center text-muted-foreground">No communication logs yet.</td></tr>
            )}
            {logs.map((row) => (
              <tr key={row.id} className="border-t hover:bg-slate-50" data-testid={`comm-log-${row.id}`}>
                <td className="p-2 whitespace-nowrap text-xs">{fmt(row.created_at)}</td>
                <td className="p-2">
                  {row.channel === "whatsapp" ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700"><MessageSquare className="w-3 h-3" /> WA</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-blue-700"><Mail className="w-3 h-3" /> Email</span>
                  )}
                </td>
                <td className="p-2 text-xs">{STAGE_LABELS[row.event_key] || row.event_key}</td>
                <td className="p-2 text-xs">{row.to || row.phone_e164}</td>
                <td className="p-2 text-xs max-w-xs truncate">{row.campaign_name || row.subject || "—"}</td>
                <td className="p-2"><StatusBadge status={row.status} /></td>
                <td className="p-2 text-xs text-red-700 max-w-xs truncate">{row.error_message || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============== Manual Send Tab ==============
function ManualSendTab() {
  const [whatsappForm, setWhatsappForm] = useState({
    to_phone: "", campaign_name: "", user_name: "Candidate", template_params_csv: "",
  });
  const [emailForm, setEmailForm] = useState({
    to_email: "", subject: "", html_body: "",
  });

  const sendWA = async () => {
    try {
      await axios.post(`${API}/api/communication/send-whatsapp`, {
        ...whatsappForm,
        template_params: whatsappForm.template_params_csv.split(",").map((s) => s.trim()).filter(Boolean),
        event_key: "manual",
      });
      toast.success("WhatsApp queued — check Logs tab");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const sendEM = async () => {
    try {
      await axios.post(`${API}/api/communication/send-email`, { ...emailForm, event_key: "manual" });
      toast.success("Email queued — check Logs tab");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="comm-manual-tab">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4 text-emerald-600" /> Send WhatsApp (test)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Input placeholder="To (mobile)" value={whatsappForm.to_phone}
                 onChange={(e) => setWhatsappForm((p) => ({ ...p, to_phone: e.target.value }))}
                 data-testid="manual-wa-phone" />
          <Input placeholder="AiSensy Campaign Name" value={whatsappForm.campaign_name}
                 onChange={(e) => setWhatsappForm((p) => ({ ...p, campaign_name: e.target.value }))}
                 data-testid="manual-wa-campaign" />
          <Input placeholder="User name" value={whatsappForm.user_name}
                 onChange={(e) => setWhatsappForm((p) => ({ ...p, user_name: e.target.value }))} />
          <Input placeholder="Template params (comma separated)" value={whatsappForm.template_params_csv}
                 onChange={(e) => setWhatsappForm((p) => ({ ...p, template_params_csv: e.target.value }))} />
          <Button onClick={sendWA} className="bg-emerald-600 hover:bg-emerald-700 w-full"
                  data-testid="manual-wa-send">
            <Send className="w-4 h-4 mr-1" /> Send WhatsApp
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /> Send Email (test)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Input placeholder="To (email)" type="email" value={emailForm.to_email}
                 onChange={(e) => setEmailForm((p) => ({ ...p, to_email: e.target.value }))}
                 data-testid="manual-em-to" />
          <Input placeholder="Subject" value={emailForm.subject}
                 onChange={(e) => setEmailForm((p) => ({ ...p, subject: e.target.value }))}
                 data-testid="manual-em-subject" />
          <Textarea placeholder="HTML body" rows={6} value={emailForm.html_body}
                    onChange={(e) => setEmailForm((p) => ({ ...p, html_body: e.target.value }))}
                    data-testid="manual-em-body" />
          <Button onClick={sendEM} className="bg-blue-600 hover:bg-blue-700 w-full"
                  data-testid="manual-em-send">
            <Send className="w-4 h-4 mr-1" /> Send Email
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ============== Root ==============
export default function CommunicationSettings() {
  return (
    <div className="space-y-4" data-testid="communication-settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <CheckCircle2 className="w-6 h-6 text-bravura" />
          Recruitment Communications
        </h1>
        <p className="text-sm text-muted-foreground">
          Candidate stage transitions par WhatsApp aur Email automatic dispatch ke liye templates configure karein.
        </p>
      </div>
      <Tabs defaultValue="whatsapp" className="w-full">
        <TabsList>
          <TabsTrigger value="whatsapp" data-testid="comm-tab-whatsapp">
            <MessageSquare className="w-4 h-4 mr-1" /> WhatsApp
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="comm-tab-email">
            <Mail className="w-4 h-4 mr-1" /> Email
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="comm-tab-logs">
            <ListChecks className="w-4 h-4 mr-1" /> Logs
          </TabsTrigger>
          <TabsTrigger value="manual" data-testid="comm-tab-manual">
            <Send className="w-4 h-4 mr-1" /> Manual Send
          </TabsTrigger>
        </TabsList>
        <TabsContent value="whatsapp"><WhatsappTab /></TabsContent>
        <TabsContent value="email"><EmailTab /></TabsContent>
        <TabsContent value="logs"><LogsTab /></TabsContent>
        <TabsContent value="manual"><ManualSendTab /></TabsContent>
      </Tabs>
    </div>
  );
}
